import type { ToolDefinition, ToolType } from "../types";

const IMAGE_DPI_CHECKER_TOOL_TYPE = {
  label: "Free DPI Analysis Tool",
  icon: "ScanSearch",
} as const satisfies ToolType;

const imageDpiCheckerTool: ToolDefinition = {
  slug: "image-dpi-checker",
  name: "Free Image DPI & PPI Checker Online | Check Photo Print Resolution",
  title: "Image DPI Checker",
  description:
    "Check image DPI and PPI instantly online. Free tool to analyze print quality, pixel dimensions, and verify if your photo meets the 300 DPI professional print standard.",
  longDescription:
    "Verify if your image is print-ready with our free online DPI checker. Instantly read the embedded X and Y DPI values, file format, and exact pixel dimensions of your uploaded photos. Ensure your artwork, documents, and posters meet the 300 DPI professional standard before you send them to the printer.",
  toolType: {
    label: "DPI Inspector",
    icon: "ScanSearch",
  },
  heroBadges: [
    "100% Free",
    "No Account Required",
    "Read-Only Analysis",
    "Instant DPI Scan",
    "No File Changes",
    "Works on Mobile",
  ],
  category: "Analysis Tools",
  categorySlug: "image-analysis-tools",
  href: "/tools/image-dpi-checker",
  api: "/api/v1/image-dpi-checker",
  keywords: [
    "snappy image resolution checker",
    "how to determine picture resolution",
    "pixels to print size calculator",
    "exif data viewer software",
    "is my photo 300 dpi",
    "convert web picture to high res",
    "print quality analyzer app",
    "find png metadata online",
    "change picture dpi maker",
    "verify jpeg print density",
  ],
  pageContent: {
    sections: {
      steps: {
        title: "How to Check Your Image DPI",
        description:
          "Follow these simple steps to analyze your image's metadata and determine its exact print resolution — no software installation needed.",
      },
      benefits: {
        title: "Why Use Our Print Quality Checker?",
        description:
          "Avoid blurry prints and rejected uploads by inspecting your image's true embedded print density and pixel dimensions instantly.",
      },
      faqs: {
        title: "Frequently Asked Questions",
        description:
          "Learn more about DPI, PPI, image resolution, and how to interpret your results for print and screen use.",
      },
    },
    introBlocks: [
      {
        kind: "text",
        title: "Check Image DPI and PPI Before You Print",
        level: "h2",
        paragraphs: [
          "Before sending any file to a commercial printer or uploading artwork to a canvas site, it is crucial to verify its print density. Our Snappy image resolution checker acts as a complete print quality analyzer app, instantly reading the embedded metadata of your photos. If you have ever stared at a file and wondered, 'is my photo 300 dpi?', this tool gives you the exact answer in seconds — without needing to open Photoshop or install any exif data viewer software.",
          "Whether you need to find png metadata online, verify jpeg print density for a professional poster, or check the PPI of an image for screen display, this analyzer extracts the X and Y DPI values directly from the file's header. By revealing the exact pixel width and height alongside the embedded resolution, it gives you the baseline numbers you need to use a pixels to print size calculator — helping you figure out precisely how large you can print before it looks blurry.",
        ],
        note: "This tool performs a read-only analysis. It does not alter your original image or output a changed file.",
      },
      {
        kind: "callouts",
        groups: [
          {
            title: "What the Analyzer Checks",
            layout: "list",
            iconColor: "primary",
            items: [
              {
                label: "Horizontal & Vertical DPI / PPI",
                icon: "Maximize",
                description:
                  "Reads the exact Dots Per Inch (DPI) and Pixels Per Inch (PPI) encoded in the file for both X and Y axes.",
                accent: "primary",
              },
              {
                label: "Pixel Dimensions",
                icon: "Scaling",
                description:
                  "Checks the true width and height of your image in pixels, ignoring digital zoom or display scaling.",
                accent: "primary",
              },
              {
                label: "File Format Verification",
                icon: "FileCode",
                description:
                  "Confirms the actual underlying file type — useful for verifying a file hasn't simply been renamed.",
                accent: "primary",
              },
            ],
          },
        ],
      },
      {
        kind: "text",
        title: "Understanding Your DPI and PPI Results",
        level: "h2",
        paragraphs: [
          "Once the analysis is complete, you need to know how to interpret the output. DPI (Dots Per Inch) tells a physical printer how densely to pack pixels onto paper. PPI (Pixels Per Inch) is the screen equivalent — it describes how tightly pixels are packed on a display. Although the terms are often used interchangeably, they describe different contexts: DPI governs print output, while PPI governs screen sharpness. Our tool reads whichever value is embedded in your file's metadata and reports both X and Y axes independently.",
          "If your image returns a result of 300 DPI or higher, it meets the gold standard for high-quality commercial printing. If the result is 72 or 96 DPI, it is formatted for the web — it may look sharp on screen but will print blurry. If your image lacks the necessary density, you can use our companion Image DPI Changer tool to rewrite the embedded tag from 72 to 300 without altering any actual pixel data.",
        ],
      },
      {
        kind: "table",
        tables: [
          {
            title: "DPI / PPI Reference Guide for Print and Screen",
            description:
              "Use this table to determine whether your image's resolution meets the requirements for your target output.",
            columns: ["DPI / PPI", "Primary Use Case", "Quality Verdict"],
            rows: [
              {
                "DPI / PPI": "72 – 96",
                "Primary Use Case":
                  "Websites, social media, email, and digital displays.",
                "Quality Verdict": "Poor for print — sharp on screen only",
              },
              {
                "DPI / PPI": "150 – 200",
                "Primary Use Case":
                  "Large-format posters and banners viewed from a distance.",
                "Quality Verdict": "Acceptable — draft or display quality",
              },
              {
                "DPI / PPI": "300",
                "Primary Use Case":
                  "Photographs, magazines, brochures, and standard documents.",
                "Quality Verdict": "Excellent — professional print standard",
              },
              {
                "DPI / PPI": "600+",
                "Primary Use Case":
                  "Fine art reproduction, archival scanning, and crisp line art.",
                "Quality Verdict": "Maximum — archival quality",
              },
            ],
            note: "Some images downloaded from social media have all metadata stripped and may return a null DPI value. Printers will usually default to 72 DPI in that case.",
            emphasisColumns: ["DPI / PPI"],
            monospaceColumns: [],
          },
        ],
      },
    ],
    steps: [
      {
        number: "01",
        title: "Upload your image",
        description:
          "Drag and drop your image onto the upload zone or click to browse. The tool supports JPG, PNG, TIFF, and PSD — the formats most commonly used in photography and professional print production.",
      },
      {
        number: "02",
        title: "Click Analyze DPI Quality",
        description:
          "Hit the button and watch the progress bar as the image is securely uploaded and its metadata is read. The analysis completes in seconds, even for large, uncompressed TIFF files.",
      },
      {
        number: "03",
        title: "Review your DPI and PPI results",
        description:
          "The results dashboard displays your current DPI and PPI values, exact pixel resolution (width × height), and file format. A visual quality indicator shows immediately whether your image is considered print-ready.",
      },
      {
        number: "04",
        title: "Act on the results",
        description:
          "If your image is print-ready (≥300 DPI), you are good to go. If it shows web quality (below 300 DPI), use a higher-resolution source or adjust the DPI tag with our companion DPI Changer tool.",
      },
    ],
    benefits: [
      {
        icon: "Eye",
        title: "100% Read-Only Safety",
        description:
          "We only analyze your file's metadata. Your original image is never altered, compressed, or resized during the inspection process.",
      },
      {
        icon: "Zap",
        title: "Instant Results — No Photoshop Needed",
        description:
          "Skip the heavy design applications. Determine picture resolution, DPI, and PPI in under 3 seconds — right from your browser.",
      },
      {
        icon: "Printer",
        title: "Print Confidence",
        description:
          "Avoid costly re-prints by verifying that your client files and personal photographs meet commercial printer requirements before submission.",
      },
      {
        icon: "FileSearch",
        title: "Independent X and Y Axis Reporting",
        description:
          "View separate horizontal and vertical DPI values to catch images that have been improperly stretched or distorted in previous software.",
      },
      {
        icon: "Smartphone",
        title: "Works on Any Device",
        description:
          "Check the DPI of an image on your phone, tablet, or desktop — no app download or account required. Works directly in any modern browser.",
      },
    ],
    faqs: [
      {
        question: "What is the difference between DPI and PPI?",
        answer:
          "DPI (Dots Per Inch) is a print measurement — it tells a physical printer how many ink dots to place per inch of paper. PPI (Pixels Per Inch) is the screen equivalent — it describes how many pixels are packed into one inch of a display. In practice, image files store a single resolution value in their metadata, and most tools (including this one) report it as DPI. When checking an image for print, both terms refer to the same embedded number. For screen output, PPI is the more accurate term.",
      },
      {
        question:
          "How do I know if my photo is high enough resolution for a canvas print?",
        answer:
          "Upload your photo to our DPI checker. First, check that the DPI is at least 150 (though 300 is preferred for small canvases). Then look at the pixel dimensions. To find the maximum print size, divide the pixel width and height by your target DPI. For example, a 3000×4500 pixel image at 300 DPI will print cleanly at 10×15 inches.",
      },
      {
        question: "How do I check DPI without Photoshop?",
        answer:
          "Simply upload your image to this tool. It reads the embedded DPI metadata directly from the file header — no Photoshop, no desktop software, and no account required. The result appears in seconds and shows both horizontal and vertical DPI values alongside the pixel dimensions.",
      },
      {
        question: "Why does my image say 'null' or show missing DPI?",
        answer:
          "DPI is a piece of text metadata embedded in an image file. If you downloaded the image from a social media platform (like Facebook or Instagram) or ran it through a web compressor, that metadata is often deliberately stripped out to reduce file size. Without this data, printers usually default to assuming it is 72 DPI.",
      },
      {
        question: "How to determine picture resolution using this tool?",
        answer:
          "Drag and drop your file into the upload zone and click Analyze. The results dashboard will show the exact pixel Width and Height. This absolute pixel count is your true picture resolution — it dictates how large the image can be printed or displayed before losing sharpness.",
      },
      {
        question: "Is there a free way to check image PPI on my phone?",
        answer:
          "Yes — this tool works fully on mobile browsers. Upload your image directly from your phone's camera roll or file storage. The analyzer will report the embedded PPI value alongside pixel dimensions and file format, with no app download required.",
      },
      {
        question:
          "Why does image DPI matter for professional printing but not for social media?",
        answer:
          "Digital screens only care about pixel dimensions (e.g., 1080×1080 pixels for an Instagram post) — they ignore DPI metadata entirely. Printers, however, use the DPI value to determine how tightly to spray ink onto paper. A 72 DPI tag tells the printer to spread pixels far apart, resulting in a large, blurry print, even if the image looks sharp on screen.",
      },
      {
        question:
          "How can I convert my image to 300 DPI if the checker shows 72 DPI?",
        answer:
          "If our checker flags your image as 72 DPI, you cannot add new pixel detail — but if the pixel dimensions are large enough, you can safely rewrite the DPI metadata tag using our companion Image DPI Changer tool. It changes the embedded value from 72 to 300 without altering the actual pixels or reducing quality.",
      },
      {
        question: "What is the difference between image resolution and DPI?",
        answer:
          "Image resolution refers to the total number of pixels in the file (e.g., 1920×1080). DPI is a separate physical instruction that tells a printer how many of those pixels to fit into one square inch of paper. A high-resolution image at 72 DPI will still print blurry — the pixel count and the DPI tag need to work together for good print output.",
      },
    ],
    useCases: [
      {
        label: "Graphic Designers",
        icon: "PenTool",
        description:
          "Quickly verify that client-supplied assets are genuinely 300 DPI before placing them into a print-ready InDesign or Illustrator layout.",
        accent: "primary",
      },
      {
        label: "Home Crafters",
        icon: "Scissors",
        description:
          "Ensure downloaded digital art from Etsy or Pinterest has high enough resolution to print clearly on home inkjet printers without blurring.",
        accent: "primary",
      },
      {
        label: "Photographers",
        icon: "Camera",
        description:
          "Double-check portfolio exports to guarantee client delivery files have retained the correct print density and haven't been stripped by a compressor.",
        accent: "primary",
      },
    ],
    audiences: [
      {
        label: "Commercial Printers",
        icon: "Printer",
        description:
          "Use the tool as a quick pre-flight image quality tester to explain low-resolution warnings to customers supplying poor-quality files.",
        accent: "accent",
      },
      {
        label: "Web Developers",
        icon: "Code",
        description:
          "Inspect images destined for the web to ensure they aren't unnecessarily bloated with 300 DPI print tags that waste bandwidth without benefit.",
        accent: "accent",
      },
      {
        label: "Archivists",
        icon: "Archive",
        description:
          "Confirm that historical document scans saved the 600+ DPI target density required for archival digital preservation standards.",
        accent: "accent",
      },
    ],
  },
};

export default imageDpiCheckerTool;
export { imageDpiCheckerTool };

// import type { ToolDefinition, ToolType } from "../types";

// const IMAGE_DPI_CHECKER_TOOL_TYPE = {
//   label: "Free DPI Analysis Tool",
//   icon: "ScanSearch",
// } as const satisfies ToolType;

// const imageDpiCheckerTool: ToolDefinition = {
//   slug: "image-dpi-checker",
//   name: "Free Image DPI Checker Online | Check Photo Print Resolution",
//   title: "Image DPI Checker",
//   description:
//     "Check image DPI and pixel resolution instantly online. Free tool to analyze print quality, view embedded metadata, and verify if your photo is 300 DPI.",
//   longDescription:
//     "Verify if your image is print-ready with our free online DPI checker. Instantly read the embedded X and Y DPI values, file format, and exact pixel dimensions of your uploaded photos. Ensure your artwork, documents, and posters meet the 300 DPI professional standard before you send them to the printer.",
//   toolType: {
//     label: "DPI Inspector",
//     icon: "Search",
//   },
//   heroBadges: [
//     "100% Free",
//     "No Account Required",
//     "Read-Only Analysis",
//     "Instant DPI Scan",
//     "No File Changes",
//   ],
//   category: "Analysis Tools",
//   categorySlug: "image-analysis-tools",
//   href: "/tools/image-dpi-checker",
//   api: "/api/v1/image-dpi-checker",
//   keywords: [
//     "snappy image resolution checker",
//     "how to determine picture resolution",
//     "pixels to print size calculator",
//     "exif data viewer software",
//     "is my photo 300 dpi",
//     "convert web picture to high res",
//     "print quality analyzer app",
//     "find png metadata online",
//     "change picture dpi maker",
//     "verify jpeg print density",
//   ],
//   pageContent: {
//     sections: {
//       steps: {
//         title: "How to Check Your Image DPI",
//         description:
//           "Follow these simple steps to analyze your image's metadata and determine its exact print resolution.",
//       },
//       benefits: {
//         title: "Why Use Our Print Quality Checker?",
//         description:
//           "Avoid blurry prints and rejected uploads by inspecting your image's true embedded print density instantly.",
//       },
//       faqs: {
//         title: "Frequently Asked Questions",
//         description:
//           "Learn more about image resolution, the difference between web and print quality, and how to interpret your DPI results.",
//       },
//     },
//     introBlocks: [
//       {
//         kind: "text",
//         title: "Check Image DPI Before You Print",
//         level: "h2",
//         paragraphs: [
//           "Before sending any file to a commercial printer or uploading artwork to a canvas site, it is crucial to verify its print density. Our Snappy image resolution checker acts as a complete print quality analyzer app, instantly reading the embedded metadata of your photos. If you have ever stared at a file and wondered, 'is my photo 300 dpi?', this tool gives you the exact answer in seconds. You don't need to purchase or install heavy exif data viewer software—just upload your file securely from your browser.",
//           "Whether you need to find png metadata online or verify jpeg print density for a professional poster, this analyzer extracts the X and Y DPI values directly from the file's header. Furthermore, by revealing the exact pixel width and height, it provides the baseline numbers you need to use a pixels to print size calculator, helping you figure out precisely how large you can print the image before it becomes unacceptably blurry.",
//         ],
//         note: "This tool performs a read-only analysis. It does not alter your original image or output a changed file.",
//       },
//       {
//         kind: "callouts",
//         groups: [
//           {
//             title: "What the Analyzer Checks",
//             layout: "list",
//             iconColor: "primary",
//             items: [
//               {
//                 label: "Horizontal & Vertical DPI",
//                 icon: "Maximize",
//                 description:
//                   "Reads the exact Dots Per Inch encoded in the file for both X and Y axes.",
//                 accent: "primary",
//               },
//               {
//                 label: "Pixel Dimensions",
//                 icon: "Scaling",
//                 description:
//                   "Checks the true width and height of your image in pixels, ignoring digital zoom.",
//                 accent: "primary",
//               },
//               {
//                 label: "File Format Verification",
//                 icon: "FileCode",
//                 description:
//                   "Confirms the actual underlying file type (e.g., verifying a file isn't just renamed).",
//                 accent: "primary",
//               },
//             ],
//           },
//         ],
//       },
//       {
//         kind: "text",
//         title: "Understanding Your DPI Results",
//         level: "h2",
//         paragraphs: [
//           "Once the analysis is complete, you need to know how to interpret the results. DPI (Dots Per Inch) tells a physical printer how densely to pack the pixels on paper. A standard computer monitor only requires 72 DPI to look sharp, which is why an image downloaded from Facebook might look great on your screen but print out as a pixelated mess.",
//           "If your image returns a result of 300 DPI or higher, it meets the gold standard for high-quality commercial printing. If the result is 72 or 96 DPI, it is formatted for the web. If you discover your image lacks the necessary density, you can easily use our companion change picture dpi maker tool to convert your web picture to a high res print standard.",
//         ],
//       },
//       {
//         kind: "table",
//         tables: [
//           {
//             title: "Print Quality Reference Guide",
//             description:
//               "Use this table to determine if the DPI result from our checker meets your project's requirements.",
//             columns: [
//               "DPI Range",
//               "Primary Use Case",
//               "Print Quality Indicator",
//             ],
//             rows: [
//               {
//                 "DPI Range": "72 - 96 DPI",
//                 "Primary Use Case":
//                   "Websites, social media, email, and digital displays.",
//                 "Print Quality Indicator": "Poor (Will look blurry if printed)",
//               },
//               {
//                 "DPI Range": "150 - 200 DPI",
//                 "Primary Use Case":
//                   "Large format posters and banners viewed from a distance.",
//                 "Print Quality Indicator": "Acceptable (Draft quality)",
//               },
//               {
//                 "DPI Range": "300 DPI",
//                 "Primary Use Case":
//                   "Photographs, magazines, brochures, and standard documents.",
//                 "Print Quality Indicator": "Excellent (Professional Standard)",
//               },
//               {
//                 "DPI Range": "600+ DPI",
//                 "Primary Use Case":
//                   "Fine art reproduction, archival scanning, and crisp line art.",
//                 "Print Quality Indicator": "Maximum (Archival Standard)",
//               },
//             ],
//             note: "Some images (especially those downloaded from social media) have all metadata stripped and may return a 'null' DPI value.",
//             emphasisColumns: ["DPI Range"],
//             monospaceColumns: [],
//           },
//         ],
//       },
//     ],
//     steps: [
//       {
//         number: "01",
//         title: "Upload your image",
//         description:
//           "Drag and drop your image onto the upload zone or click to browse. The tool supports JPG, PNG, TIFF, and PSD — the formats most commonly used in photography and professional print production.",
//       },
//       {
//         number: "02",
//         title: "Click Analyze DPI Quality",
//         description:
//           "Hit the button and watch the progress bar as the image is securely uploaded and its metadata is read. The analysis completes in seconds, even for large, uncompressed TIFF files.",
//       },
//       {
//         number: "03",
//         title: "Review your DPI results",
//         description:
//           "The results dashboard displays your current DPI, exact pixel resolution (width × height), and file format. A visual quality indicator shows immediately whether your image is considered print-ready.",
//       },
//       {
//         number: "04",
//         title: "Act on the results",
//         description:
//           "If your image is print-ready (≥300 DPI), you are good to go. If it shows web quality (below 300 DPI), you may need to use a higher-resolution source image or manually adjust the DPI tag using our DPI Changer tool.",
//       },
//     ],
//     benefits: [
//       {
//         icon: "Eye",
//         title: "100% Read-Only Safety",
//         description:
//           "We only analyze your file's metadata. Your original image is never altered, compressed, or resized during the inspection process.",
//       },
//       {
//         icon: "Zap",
//         title: "Instant Results",
//         description:
//           "Stop guessing or waiting for heavy design applications to load. Determine picture resolution and print quality in under 3 seconds.",
//       },
//       {
//         icon: "Printer",
//         title: "Print Confidence",
//         description:
//           "Avoid costly re-prints by verifying that your client files and personal photographs meet commercial printer requirements before submission.",
//       },
//       {
//         icon: "FileSearch",
//         title: "Detailed Metadata",
//         description:
//           "View independent X and Y DPI values, ensuring your image hasn't been improperly stretched or distorted in previous software.",
//       },
//     ],
//     faqs: [
//       {
//         question:
//           "How do I know if my photo is high enough resolution for a canvas print?",
//         answer:
//           "Upload your photo to our DPI checker. First, check if the DPI is at least 150 (though 300 is preferred for small canvases). Next, look at the pixel dimensions. To find the maximum print size in inches, divide the pixel width and height by your target DPI. For example, a 3000x4500 pixel image printed at 300 DPI will make a perfect 10x15 inch canvas.",
//       },
//       {
//         question:
//           "Why does the checker say my image has 'null' or missing DPI?",
//         answer:
//           "DPI is a piece of text metadata embedded in an image file. If you downloaded the image from a social media platform (like Facebook or Instagram) or ran it through a web compressor, that metadata is often deliberately stripped out to save file space. Without this data, printers usually default to assuming it is 72 DPI.",
//       },
//       {
//         question: "How to determine picture resolution using this tool?",
//         answer:
//           "Simply drag and drop your file into the upload zone and hit analyze. The results dashboard will explicitly state the 'Width' and 'Height' in exact pixels. This absolute pixel count represents your true picture resolution, which dictates how large the image can be displayed or printed.",
//       },
//       {
//         question:
//           "Why does image DPI matter for professional printing but not for social media?",
//         answer:
//           "Digital screens (phones, monitors) do not read DPI metadata; they only care about pixel dimensions (e.g., 1080x1080 pixels for an Instagram post). Printers, however, need to know how tightly to spray ink dots onto physical paper. If a printer receives an image with a 72 DPI tag, it will spread the pixels far apart, resulting in a large, blurry print.",
//       },
//       {
//         question:
//           "How can I convert my web picture to high res if the checker says it's 72 DPI?",
//         answer:
//           "If our checker flags your image as 72 DPI, you cannot physically add new details to the image here. However, if the pixel dimensions are large enough, you can use our 'Image DPI Changer' tool to safely rewrite the internal metadata tag from 72 to 300 without losing any quality.",
//       },
//       {
//         question: "What is the difference between image resolution and DPI?",
//         answer:
//           "Image resolution refers to the total number of digital pixels in the file (e.g., 1920x1080). DPI (Dots Per Inch) is a physical measurement instruction that tells a printer how many of those pixels to cram into one square inch of paper. They work together to determine final print quality.",
//       },
//       {
//         question:
//           "Can I view the original camera settings and GPS location from a photo online?",
//         answer:
//           "This specific tool focuses exclusively on checking DPI, format, and dimensions for print readiness. If you want to dive deeper into full camera settings (shutter speed, aperture, GPS coordinates), you should use our dedicated full 'Image Analyzer' or 'EXIF Data Viewer' tool.",
//       },
//     ],
//     useCases: [
//       {
//         label: "Graphic Designers",
//         icon: "PenTool",
//         description:
//           "Quickly verify that assets supplied by a client are actually 300 DPI before placing them into a print-ready InDesign layout.",
//         accent: "primary",
//       },
//       {
//         label: "Home Crafters",
//         icon: "Scissors",
//         description:
//           "Ensure downloaded digital art from Etsy or Pinterest has high enough resolution to print clearly on home inkjet printers.",
//         accent: "primary",
//       },
//       {
//         label: "Photographers",
//         icon: "Camera",
//         description:
//           "Double-check portfolio exports to guarantee client delivery files have retained the correct print density tags.",
//         accent: "primary",
//       },
//     ],
//     audiences: [
//       {
//         label: "Commercial Printers",
//         icon: "Printer",
//         description:
//           "Use the tool as a quick pre-flight check to explain 'low resolution' warnings to customers supplying poor-quality files.",
//         accent: "accent",
//       },
//       {
//         label: "Web Developers",
//         icon: "Code",
//         description:
//           "Inspect images meant for the web to ensure they aren't unnecessarily bloated with 300 DPI print tags that waste bandwidth.",
//         accent: "accent",
//       },
//       {
//         label: "Archivists",
//         icon: "Archive",
//         description:
//           "Confirm that historical document scans successfully saved the 600+ DPI target density required for digital preservation.",
//         accent: "accent",
//       },
//     ],
//   },
// };

// export default imageDpiCheckerTool;
// export { imageDpiCheckerTool };
